import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import moment from "moment";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";

function unwrapResult(response) {
  if (response?.Success === false) {
    throw new Error(
      response?.ErrorMessage ||
        response?.ValidationMessage ||
        "Erro na requisição.",
    );
  }
  return response?.Result ?? response?.result ?? response;
}

function TimelineEvent({ entry, isLast }) {
  return (
    <Box sx={{ display: "flex", gap: 2, position: "relative", pb: isLast ? 0 : 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          width: 40,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: themeColorsDesingSystem.secondary.active,
            color: themeColorsDesingSystem.primary.main,
            zIndex: 1,
          }}
        >
          <HistoryIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${themeColorsDesingSystem.stone.main}`,
          borderRadius: "8px",
          bgcolor: themeColorsDesingSystem.white.main,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: themeColorsDesingSystem.primary.main,
            }}
          >
            Alteração
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
              color: themeColorsDesingSystem.gray.main,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {entry?.DataAlteracao
                ? moment(entry.DataAlteracao).format("DD/MM/YYYY HH:mm")
                : "—"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 1.5, bgcolor: themeColorsDesingSystem.stone.light }}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: themeColorsDesingSystem.gray.dark,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
            }}
          >
            {entry?.DescricaoAlteracao || "Sem descrição."}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 2,
            py: 1.25,
            color: themeColorsDesingSystem.gray.main,
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: "0.8rem" }}>
            Por {entry?.NomeUsuario || "Usuário desconhecido"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function HistoricoModal({ open, onClose, solicitacao }) {
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [historico, setHistorico] = useState([]);

  useEffect(() => {
    if (!open || !solicitacao?.IdSolicitacaoDevolucaoInterna) return;

    async function loadHistorico() {
      setLoading(true);
      try {
        const res = await Api.get(
          "detail",
          "internalReturn",
          `?id=${solicitacao.IdSolicitacaoDevolucaoInterna}`,
          true,
        );
        const data = unwrapResult(res);
        setHistorico(data?.Historico || []);
      } catch (error) {
        snackbar.showMessage(
          error?.message || "Não foi possível carregar o histórico.",
          { severity: "error" },
        );
        setHistorico([]);
      } finally {
        setLoading(false);
      }
    }

    loadHistorico();
  }, [open, solicitacao]);

  useEffect(() => {
    if (!open) {
      setHistorico([]);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 640,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          pt={2.5}
          pb={2}
          sx={{ borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}` }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: themeColorsDesingSystem.primary.main,
              }}
            >
              Histórico de Alterações
            </Typography>
            {solicitacao?.NumeroNota && (
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: themeColorsDesingSystem.gray.main,
                  mt: 0.25,
                }}
              >
                NF {solicitacao.NumeroNota} · {solicitacao.RazaoSocial}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="fechar" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={6}
            >
              <CircularProgress size={28} />
            </Box>
          ) : historico.length === 0 ? (
            <Typography color="textSecondary" align="center" py={4}>
              Nenhum registro de histórico encontrado para esta solicitação.
            </Typography>
          ) : (
            <Box sx={{ position: "relative", pl: 0.5 }}>
              <Box
                sx={{
                  position: "absolute",
                  left: 18,
                  top: 18,
                  bottom: 18,
                  width: 2,
                  bgcolor: themeColorsDesingSystem.stone.main,
                }}
              />
              {historico.map((entry, index) => (
                <TimelineEvent
                  key={
                    entry.IdSolicitacaoDevolucaoInternaHistorico ??
                    `${entry.DataAlteracao}-${index}`
                  }
                  entry={entry}
                  isLast={index === historico.length - 1}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
